import { isFlexibleScenario } from "@reco/service/src/utils/flexibleScenarios";
import { Scenario, type ScenarioType } from "@reco/shared/src/constants";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { useI18n } from "vue-i18n";

export type UseHeadingParams = {
  scenario: ScenarioType;
  headingProp?: string;
  brand?: string;
  categoryName?: string;
  category?: string;
};

/**
 * Get the heading for the component based on scenario and props
 * @param paramsOrGetter - The parameters for the heading, or a getter function returning them
 * @returns The content of the heading as a computed ref
 */
export function useHeading(paramsOrGetter: MaybeRefOrGetter<UseHeadingParams>) {
  const { t, te } = useI18n();

  return computed(() => {
    const { scenario, headingProp, brand, categoryName, category } = toValue(paramsOrGetter);

    const effectiveCategory = categoryName ?? category;
    const isFlex = isFlexibleScenario(scenario);
    const scenarioHeadingKey = `reco-frontend-next.Slider.${scenario}.heading`;

    const strategies: Array<{ condition: boolean; resolve: () => string }> = [
      {
        condition: scenario === Scenario.PERSONAL_BRAND && !!brand && te("reco-frontend-next.Slider.personal_brand"),
        resolve: () => t("reco-frontend-next.Slider.personal_brand", { brand }),
      },
      {
        condition:
          scenario === Scenario.PERSONAL_CATEGORY &&
          !!effectiveCategory &&
          te("reco-frontend-next.Slider.personal_category"),
        resolve: () => t("reco-frontend-next.Slider.personal_category", { category: effectiveCategory }),
      },
      {
        condition: !!headingProp,
        resolve: () => headingProp!,
      },
      {
        condition: scenario === Scenario.CROSS && te("reco-frontend-next.Slider.cross"),
        resolve: () => t("reco-frontend-next.Slider.cross"),
      },
      {
        condition: isFlex && !!brand && te("reco-frontend-next.Slider.personal_brand"),
        resolve: () => t("reco-frontend-next.Slider.personal_brand", { brand }),
      },
      {
        condition: isFlex && !!effectiveCategory && te("reco-frontend-next.Slider.personal_category"),
        resolve: () => t("reco-frontend-next.Slider.personal_category", { category: effectiveCategory }),
      },
      {
        condition: te(scenarioHeadingKey),
        resolve: () => t(scenarioHeadingKey),
      },
    ];

    return strategies.find((strategy) => strategy.condition)?.resolve() ?? "";
  });
}
